import axios from 'axios';
import { Configuration } from 'src/openapi/configuration';
import type { JobApiBqAudienceExportApiFormatEnum } from 'src/openapi/apis/job-api-api';
import type { AiInviteResponse } from 'src/pages/skillBuilder/types/Multiplayer';
import type {
  AiInviteRunResult,
  AiInviteSettingsResponse,
} from 'src/redux/types/globalVars';
import type {
  GetCompetitionQuestionsSub,
  GetCustomCourseResponse,
  PendingCertificateClaimSub,
  PremiumPromoSendBody,
} from 'src/openapi';
import type {
  IInterest,
  IPositionOfResponsibility,
} from 'src/components/candidateProfile/types';
import type {
  ProfileCompletion,
  ProfileCompletionItemKey,
} from 'src/redux/types/candidateProfile';
import type {
  FreeUnlockAudienceReach,
  FreeUnlockEmailReach,
  FreeUnlockEmailReachParams,
  FreeUnlockAudienceRules,
  FreeUnlockOffer,
  FreeUnlockOfferInput,
  FreeUnlockOfferInvite,
  FreeUnlockOfferInvitesChange,
} from 'src/redux/types/employerCredit';
import ConfigUrl from '../../config';
import { getHeaderDeviceType } from './utils';
import setupAuthInterceptors from './authInterceptor';
import { reloadOnChunkLoadFailure } from './chunkLoadError';

/** The extension's uninstall page: sent when it opens, and again with the
 *  reasons ticked. Mirrors the backend `AutofillUninstallBodySchema`; custom
 *  type until the client is regenerated. */
export interface ExtensionUninstallBody {
  install_id: string;
  extension_version?: string | null;
  reasons?: string[];
  comment?: string | null;
}

/** What the forge charges and what the learner has to spend. Mirrors the
 *  backend `ForgePricingSchema`; custom type until the client is regenerated. */
export interface ForgePricing {
  ai_credits?: number;
  /** A chat reply. */
  chat_cost?: number;
  /** A chat message that draws or redraws the plan, instead of a reply's. */
  design_cost?: number;
  lessons_cost?: number;
  lessons_per_batch?: number;
  lessons_paid?: number;
  spent?: number;
}

/** The check the forge runs before designing a plan: its latest chat reply,
 *  and replies the learner can tap. Mirrors the backend
 *  `CustomCourseCheckSchema`; custom type until the client is regenerated. */
export interface ForgeCheck {
  /** build | ask | need_source | decline */
  decision?: string;
  reply?: string | null;
  quick_replies?: string[];
}

/** One message of the chat before design, oldest first. */
export interface ForgeChatMessage {
  role: 'learner' | 'forge';
  text: string;
}

/** Response of the unlock-next-lessons call. */
export interface ForgeUnlockResult extends ForgePricing {
  success?: boolean;
  lessons_paid?: number;
  lessons_total?: number;
}

// Composed promotion email (matches the backend PromotionEmailSchema + the
// bulk-email composer pair). Custom type until the client is regenerated.
export interface PromotionEmailPayload {
  subject?: string | null;
  body?: string | null;
  from_email?: string | null;
  from_email_name?: string | null;
  preheader?: string | null;
  plaintext_content?: string | null;
  is_html_template?: boolean;
}

/** Response of accepting an AI game invite. Mirrors the backend
 *  `AcceptAiInviteResponseSchema`; custom type until the client is regenerated. */
export interface AcceptAiInviteResult {
  success?: boolean;
  room_id?: string;
  /** Set when the accept started the round, so play can open straight away. */
  session_id?: string | null;
}

/** Response of regenerating one job-assessment question: the replacement, in the
 *  question list's shape plus its AI review. Mirrors the backend
 *  `RegenerateJobChallengeQuestionResponseSchema`; custom type until the client is
 *  regenerated. */
export interface RegeneratedJobChallengeQuestion {
  success?: boolean;
  replaced_question_id?: string;
  question?: GetCompetitionQuestionsSub & {
    ai_review_score?: number | null;
    ai_review_note?: string | null;
  };
}

// Register axios interceptors once, before any API call can be made.
// On 401 `token_expired`, calls POST /v2/token/refresh (cookie-based) and
// retries the original request with the new access token.
setupAuthInterceptors();

// Each generated API domain (src/openapi/apis/*.ts) is its own dynamic-import
// target, so Rollup/Vite splits it into its own chunk. A page that only calls
// getJobApi()/getUserApi() downloads just those two chunks, not all ~40
// domains — unlike importing everything through one shared barrel module.
// Retrying a failed chunk fetch (stale hash after a deploy) needs to clear
// the cached promise for that ONE domain only, not every domain.
function lazyApi<T>(
  load: () => Promise<{ new (config: Configuration): T }>,
  getConfig: () => Configuration,
): () => Promise<T> {
  let instance: Promise<T> | null = null;
  return () => {
    instance ??= load()
      .then((Ctor) => new Ctor(getConfig()))
      .catch((error) => {
        instance = null;
        reloadOnChunkLoadFailure(error);
        throw error;
      });
    return instance;
  };
}

class ApiClient {
  public apiKey?: string;

  getAPIConfiguration = () => {
    const baseOptions: any = {
      withCredentials: true,
    };

    const type = getHeaderDeviceType();
    if (type) {
      baseOptions.headers = { 'x-device-type': type };
    }

    // Read live, not baked in at construction — each getXxxApi() singleton is
    // built once and reused, so a snapshot string would go stale across
    // login/logout/token-refresh.
    const config = new Configuration({
      basePath: ConfigUrl.API_URL,
      apiKey: () => this.apiKey ?? '',
      accessToken: () => this.apiKey ?? '',
      baseOptions,
    });

    config.isJsonMime = () => false;

    return config;
  };

  public setApiKey = (value: string) => {
    this.apiKey = value;
  };

  public getJobApi = lazyApi(
    () => import('src/openapi/apis/job-api-api').then((m) => m.JobApiApi),
    this.getAPIConfiguration,
  );

  public getExperimentApi = lazyApi(
    () =>
      import('src/openapi/apis/experiment-api-api').then(
        (m) => m.ExperimentApiApi,
      ),
    this.getAPIConfiguration,
  );

  public getAutofillApi = lazyApi(
    () =>
      import('src/openapi/apis/autofill-chrome-extension-api-api').then(
        (m) => m.AutofillChromeExtensionApiApi,
      ),
    this.getAPIConfiguration,
  );

  public getGptApi = lazyApi(
    () => import('src/openapi/apis/gpt-api-api').then((m) => m.GptApiApi),
    this.getAPIConfiguration,
  );

  public getAssistantApi = lazyApi(
    () =>
      import('src/openapi/apis/assistant-api-api').then(
        (m) => m.AssistantApiApi,
      ),
    this.getAPIConfiguration,
  );

  public getCourseStatsApi = lazyApi(
    () =>
      import('src/openapi/apis/course-stats-api-api').then(
        (m) => m.CourseStatsApiApi,
      ),
    this.getAPIConfiguration,
  );

  public getProStatsApi = lazyApi(
    () =>
      import('src/openapi/apis/pro-stats-api-api').then(
        (m) => m.ProStatsApiApi,
      ),
    this.getAPIConfiguration,
  );

  public getReviewStatsApi = lazyApi(
    () =>
      import('src/openapi/apis/review-stats-api-api').then(
        (m) => m.ReviewStatsApiApi,
      ),
    this.getAPIConfiguration,
  );

  public getUserApi = lazyApi(
    () => import('src/openapi/apis/user-api-api').then((m) => m.UserApiApi),
    this.getAPIConfiguration,
  );

  public getOrgApi = lazyApi(
    () =>
      import('src/openapi/apis/organization-api-api').then(
        (m) => m.OrganizationApiApi,
      ),
    this.getAPIConfiguration,
  );

  public getSmsApi = lazyApi(
    () => import('src/openapi/apis/sms-api-api').then((m) => m.SmsApiApi),
    this.getAPIConfiguration,
  );

  public getFilesApi = lazyApi(
    () => import('src/openapi/apis/files-api-api').then((m) => m.FilesApiApi),
    this.getAPIConfiguration,
  );

  public getGeneralApi = lazyApi(
    () =>
      import('src/openapi/apis/general-api-api').then((m) => m.GeneralApiApi),
    this.getAPIConfiguration,
  );

  public getTokenApi = lazyApi(
    () => import('src/openapi/apis/token-api-api').then((m) => m.TokenApiApi),
    this.getAPIConfiguration,
  );

  public getLevelApi = lazyApi(
    () => import('src/openapi/apis/level-api-api').then((m) => m.LevelApiApi),
    this.getAPIConfiguration,
  );

  // The platform-wide XP ladder. Distinct from getLevelApi above, which is the
  // per-skill score ladder ("LEVEL 2 · SWIFT" on a skill page).
  public getUserLevelApi = lazyApi(
    () =>
      import('src/openapi/apis/user-level-api-api').then(
        (m) => m.UserLevelApiApi,
      ),
    this.getAPIConfiguration,
  );

  public getDailyMissionApi = lazyApi(
    () =>
      import('src/openapi/apis/daily-mission-api-api').then(
        (m) => m.DailyMissionApiApi,
      ),
    this.getAPIConfiguration,
  );

  // Time-boxed multipliers on what the XP ladder pays. Admin-only.
  public getRewardPromotionApi = lazyApi(
    () =>
      import('src/openapi/apis/reward-promotion-api-api').then(
        (m) => m.RewardPromotionApiApi,
      ),
    this.getAPIConfiguration,
  );

  public getNotificationsApi = lazyApi(
    () =>
      import('src/openapi/apis/notification-api-api').then(
        (m) => m.NotificationApiApi,
      ),
    this.getAPIConfiguration,
  );

  public getAppVersionApi = lazyApi(
    () =>
      import('src/openapi/apis/app-version-api-api').then(
        (m) => m.AppVersionApiApi,
      ),
    this.getAPIConfiguration,
  );

  public getPaymentApi = lazyApi(
    () =>
      import('src/openapi/apis/payment-api-api').then((m) => m.PaymentApiApi),
    this.getAPIConfiguration,
  );

  public getCreditsApi = lazyApi(
    () =>
      import('src/openapi/apis/credits-api-api').then((m) => m.CreditsApiApi),
    this.getAPIConfiguration,
  );

  public getMockTestApi = lazyApi(
    () =>
      import('src/openapi/apis/mocktest-api-api').then((m) => m.MocktestApiApi),
    this.getAPIConfiguration,
  );

  public getExamApi = lazyApi(
    () => import('src/openapi/apis/exam-api-api').then((m) => m.ExamApiApi),
    this.getAPIConfiguration,
  );

  public getQuestionApi = lazyApi(
    () =>
      import('src/openapi/apis/question-api-api').then((m) => m.QuestionApiApi),
    this.getAPIConfiguration,
  );

  public getMailAPi = lazyApi(
    () => import('src/openapi/apis/mail-api-api').then((m) => m.MailApiApi),
    this.getAPIConfiguration,
  );

  public getCustomCourseApi = lazyApi(
    () =>
      import('src/openapi/apis/custom-course-api-api').then(
        (m) => m.CustomCourseApiApi,
      ),
    this.getAPIConfiguration,
  );

  public getCoverLetterApi = lazyApi(
    () =>
      import('src/openapi/apis/cover-letter-api-api').then(
        (m) => m.CoverLetterApiApi,
      ),
    this.getAPIConfiguration,
  );

  public getSkillsApi = lazyApi(
    () => import('src/openapi/apis/skill-api-api').then((m) => m.SkillApiApi),
    this.getAPIConfiguration,
  );

  public getResumeApi = lazyApi(
    () => import('src/openapi/apis/resume-api-api').then((m) => m.ResumeApiApi),
    this.getAPIConfiguration,
  );

  public getScoreApi = lazyApi(
    () => import('src/openapi/apis/score-api-api').then((m) => m.ScoreApiApi),
    this.getAPIConfiguration,
  );

  public getChatApi = lazyApi(
    () => import('src/openapi/apis/chat-api-api').then((m) => m.ChatApiApi),
    this.getAPIConfiguration,
  );

  public getCompetitionApi = lazyApi(
    () =>
      import('src/openapi/apis/competition-api-api').then(
        (m) => m.CompetitionApiApi,
      ),
    this.getAPIConfiguration,
  );

  public getFairplayApi = lazyApi(
    () =>
      import('src/openapi/apis/fairplay-api-api').then((m) => m.FairplayApiApi),
    this.getAPIConfiguration,
  );

  public getCodingPracticeApi = lazyApi(
    () =>
      import('src/openapi/apis/coding-practice-api-api').then(
        (m) => m.CodingPracticeApiApi,
      ),
    this.getAPIConfiguration,
  );

  public getEmployerCreditApi = lazyApi(
    () =>
      import('src/openapi/apis/employer-credit-api-api').then(
        (m) => m.EmployerCreditApiApi,
      ),
    this.getAPIConfiguration,
  );

  public getInterviewApi = lazyApi(
    () =>
      import('src/openapi/apis/interview-api-api').then(
        (m) => m.InterviewApiApi,
      ),
    this.getAPIConfiguration,
  );

  public getStatsApi = lazyApi(
    () =>
      import('src/openapi/apis/statistics-api-api').then(
        (m) => m.StatisticsApiApi,
      ),
    this.getAPIConfiguration,
  );

  public getOutreachApi = lazyApi(
    () =>
      import('src/openapi/apis/outreach-api-api').then((m) => m.OutreachApiApi),
    this.getAPIConfiguration,
  );

  public getReviewApi = lazyApi(
    () => import('src/openapi/apis/review-api-api').then((m) => m.ReviewApiApi),
    this.getAPIConfiguration,
  );

  public getBadgeApi = lazyApi(
    () => import('src/openapi/apis/badge-api-api').then((m) => m.BadgeApiApi),
    this.getAPIConfiguration,
  );

  public getDashboardApi = lazyApi(
    () =>
      import('src/openapi/apis/dashboard-api-api').then(
        (m) => m.DashboardApiApi,
      ),
    this.getAPIConfiguration,
  );

  public getSalesApi = lazyApi(
    () => import('src/openapi/apis/sales-api-api').then((m) => m.SalesApiApi),
    this.getAPIConfiguration,
  );

  public getCouponApi = lazyApi(
    () => import('src/openapi/apis/coupon-api-api').then((m) => m.CouponApiApi),
    this.getAPIConfiguration,
  );

  public getManagementApi = lazyApi(
    () =>
      import('src/openapi/apis/management-api-api').then(
        (m) => m.ManagementApiApi,
      ),
    this.getAPIConfiguration,
  );

  public getCaApi = lazyApi(
    () => import('src/openapi/apis/ca-api-api').then((m) => m.CaApiApi),
    this.getAPIConfiguration,
  );

  public getAdminCaApi = lazyApi(
    () =>
      import('src/openapi/apis/admin-ca-api-api').then((m) => m.AdminCaApiApi),
    this.getAPIConfiguration,
  );

  public getQuizApi = lazyApi(
    () => import('src/openapi/apis/quiz-api-api').then((m) => m.QuizApiApi),
    this.getAPIConfiguration,
  );

  public getQuizReviewApi = lazyApi(
    () =>
      import('src/openapi/apis/quiz-review-api-api').then(
        (m) => m.QuizReviewApiApi,
      ),
    this.getAPIConfiguration,
  );

  public getFacultyLibraryApi = lazyApi(
    () =>
      import('src/openapi/apis/faculty-library-api-api').then(
        (m) => m.FacultyLibraryApiApi,
      ),
    this.getAPIConfiguration,
  );

  public getFacultyCourseApi = lazyApi(
    () =>
      import('src/openapi/apis/faculty-course-api-api').then(
        (m) => m.FacultyCourseApiApi,
      ),
    this.getAPIConfiguration,
  );

  public getFacultyClassApi = lazyApi(
    () =>
      import('src/openapi/apis/faculty-class-api-api').then(
        (m) => m.FacultyClassApiApi,
      ),
    this.getAPIConfiguration,
  );

  public getFacultyInviteAdminApi = lazyApi(
    () =>
      import('src/openapi/apis/faculty-invite-admin-api-api').then(
        (m) => m.FacultyInviteAdminApiApi,
      ),
    this.getAPIConfiguration,
  );

  public getFacultyInviteApi = lazyApi(
    () =>
      import('src/openapi/apis/faculty-invite-api-api').then(
        (m) => m.FacultyInviteApiApi,
      ),
    this.getAPIConfiguration,
  );

  public getClassInviteApi = lazyApi(
    () =>
      import('src/openapi/apis/class-invite-api-api').then(
        (m) => m.ClassInviteApiApi,
      ),
    this.getAPIConfiguration,
  );

  public getFacultyExamApi = lazyApi(
    () =>
      import('src/openapi/apis/faculty-exam-api-api').then(
        (m) => m.FacultyExamApiApi,
      ),
    this.getAPIConfiguration,
  );

  public getJoinedClassApi = lazyApi(
    () =>
      import('src/openapi/apis/joined-class-api-api').then(
        (m) => m.JoinedClassApiApi,
      ),
    this.getAPIConfiguration,
  );

  public getPlacementPartnerApi = lazyApi(
    () =>
      import('src/openapi/apis/placement-partner-api-api').then(
        (m) => m.PlacementPartnerApiApi,
      ),
    this.getAPIConfiguration,
  );

  public getProfileRewardsApi = lazyApi(
    () =>
      import('src/openapi/apis/profile-rewards-api-api').then(
        (m) => m.ProfileRewardsApiApi,
      ),
    this.getAPIConfiguration,
  );

  public getHiringApi = lazyApi(
    () => import('src/openapi/apis/hiring-api-api').then((m) => m.HiringApiApi),
    this.getAPIConfiguration,
  );

  public getManagedHiringApi = lazyApi(
    () =>
      import('src/openapi/apis/managed-hiring-api-api').then(
        (m) => m.ManagedHiringApiApi,
      ),
    this.getAPIConfiguration,
  );

  public getNotifyApi = lazyApi(
    () => import('src/openapi/apis/notify-api-api').then((m) => m.NotifyApiApi),
    this.getAPIConfiguration,
  );

  public getAdminNotificationApi = lazyApi(
    () =>
      import('src/openapi/apis/admin-notification-api-api').then(
        (m) => m.AdminNotificationApiApi,
      ),
    this.getAPIConfiguration,
  );

  public getAuditLogApi = lazyApi(
    () =>
      import('src/openapi/apis/audit-log-api-api').then(
        (m) => m.AuditLogApiApi,
      ),
    this.getAPIConfiguration,
  );

  public getExternalJobApi = lazyApi(
    () =>
      import('src/openapi/apis/external-job-api-api').then(
        (m) => m.ExternalJobApiApi,
      ),
    this.getAPIConfiguration,
  );

  // Distinct entity types present in the audit log (with counts, high->low) for
  // the admin filter dropdown. Custom method until the typed client is
  // regenerated from the spec.
  public getAuditLogEntityTypes = async () => {
    const config = this.getAPIConfiguration();
    const response = await axios.get(
      `${config.basePath}/admin/audit-logs/entity-types`,
      { headers: { Authorization: `Bearer ${this.apiKey}` } },
    );
    return response.data as {
      entity_types: { entity_type: string; count: number }[];
    };
  };

  // --- External jobs admin ---
  public getExternalJobPreferences = async () => {
    const api = await this.getExternalJobApi();
    const res = await api.externalJobApiGetExternalJobPreferences();
    return res.data;
  };

  // Returns the recomputed preview alongside the saved settings, so the panel
  // never has to re-fetch to show what a change did.
  public updateExternalJobPreference = async (name: string, value: string) => {
    const api = await this.getExternalJobApi();
    const res = await api.externalJobApiUpdateExternalJobPreference({
      updateExternalJobPreferenceBody: { name, value },
    });
    return res.data;
  };

  public previewExternalFetch = async (payload: any) => {
    const api = await this.getExternalJobApi();
    const res = await api.externalJobApiPreviewExternalFetch({
      fetchRequestBody: payload,
    });
    return res.data;
  };

  public runExternalFetch = async (payload: any) => {
    const api = await this.getExternalJobApi();
    const res = await api.externalJobApiRunExternalFetch({
      fetchRequestBody: payload,
    });
    return res.data;
  };

  public listExternalJobs = async (payload: any) => {
    const api = await this.getExternalJobApi();
    const res = await api.externalJobApiListExternalJobs({
      activeJobBody: payload,
    });
    return res.data;
  };

  public listExternalFetchRuns = async (page = 1, pageSize = 25) => {
    const api = await this.getExternalJobApi();
    const res = await api.externalJobApiListExternalFetchRuns({
      page,
      pageSize,
    });
    return res.data;
  };

  public listExternalCollections = async () => {
    const api = await this.getExternalJobApi();
    const res = await api.externalJobApiListExternalCollections();
    return res.data;
  };

  public seedExternalCollections = async () => {
    const api = await this.getExternalJobApi();
    const res = await api.externalJobApiSeedExternalCollections();
    return res.data;
  };

  public ingestExternalCollection = async (category: string, filters?: any) => {
    const api = await this.getExternalJobApi();
    const res = await api.externalJobApiIngestExternalCollection({
      ingestCollectionBody: { category, filters },
    });
    return res.data;
  };

  // BQ outreach audience filters. Custom axios until the typed client is
  // regenerated from the spec (the co-filtering params are new). Selections are
  // repeated query keys, which is what the backend's List params expect.
  public bqFacetOptions = async (
    field: string,
    q: string,
    selection: Record<string, string[] | boolean | null>,
  ) => {
    const config = this.getAPIConfiguration();
    const res = await axios.get(`${config.basePath}/job/bq/autocomplete`, {
      params: { field, q, ...selection },
      paramsSerializer: { indexes: null },
      headers: { Authorization: `Bearer ${this.apiKey}` },
    });
    return res.data as {
      options: { value: string; count: number }[];
      source: 'scoped' | 'partial' | 'unscoped';
    };
  };

  // Downloads the selected audience as CSV. Returns the blob plus what the
  // server actually wrote — fewer rows than requested once duplicate addresses
  // are collapsed.
  public bqAudienceExport = async (
    selection: Record<string, string[] | boolean | null>,
    limit: number,
    offset: number,
    // Type-only: the job API module is lazy-loaded, so its enum object
    // must not be pulled into this file.
    format: JobApiBqAudienceExportApiFormatEnum = 'csv',
  ) => {
    const config = this.getAPIConfiguration();
    const res = await axios.get(`${config.basePath}/job/bq/audience-export`, {
      params: { ...selection, limit, offset, format },
      paramsSerializer: { indexes: null },
      headers: { Authorization: `Bearer ${this.apiKey}` },
      responseType: 'blob',
    });
    const disposition = String(res.headers['content-disposition'] ?? '');
    return {
      blob: res.data as Blob,
      filename:
        disposition.match(/filename="?([^"]+)"?/)?.[1] ??
        `bq-audience.${format}`,
      rowCount: Number(res.headers['x-row-count'] ?? 0),
    };
  };

  // Public: the extension is gone, so there is no session to send. The
  // install's random id is all the page knows.
  public recordExtensionUninstall = async (body: ExtensionUninstallBody) => {
    const config = this.getAPIConfiguration();
    const res = await axios.post(`${config.basePath}/autofill/uninstall`, body);
    return res.data as { success: boolean; recorded: boolean };
  };

  /** The extension version live on the Web Store, or null when unknown. Custom
   *  until the client is regenerated (`GET /autofill/published-version`). */
  public getPublishedExtensionVersion = async () => {
    const config = this.getAPIConfiguration();
    const res = await axios.get(
      `${config.basePath}/autofill/published-version`,
      {
        headers: { Authorization: `Bearer ${this.apiKey}` },
      },
    );
    return res.data as { success: boolean; published_version: string | null };
  };

  public bqSendEmails = async (body: Record<string, any>) => {
    const config = this.getAPIConfiguration();
    const res = await axios.post(
      `${config.basePath}/job/bq/send_emails`,
      body,
      {
        headers: { Authorization: `Bearer ${this.apiKey}` },
      },
    );
    return res.data as { words_written: number };
  };

  public bqAudienceCount = async (
    selection: Record<string, string[] | boolean | null>,
  ) => {
    const config = this.getAPIConfiguration();
    const res = await axios.get(`${config.basePath}/job/bq/audience-count`, {
      params: selection,
      paramsSerializer: { indexes: null },
      headers: { Authorization: `Bearer ${this.apiKey}` },
    });
    return res.data as { count: number; is_exact: boolean };
  };

  // Distinct values of one potential-applicant filter column, with how many
  // applicants each covers. Custom axios until the typed client is regenerated
  // from the spec (the endpoint is new).
  public potentialApplicantFacet = async (
    field: string,
    searchQuery: string,
    pageSize = 20,
  ) => {
    const config = this.getAPIConfiguration();
    const res = await axios.get(
      `${config.basePath}/admin/potential-applicant/facet/list`,
      {
        params: {
          field,
          search_query: searchQuery,
          page: 1,
          page_size: pageSize,
          order_by_max_count: true,
        },
        headers: { Authorization: `Bearer ${this.apiKey}` },
      },
    );
    return res.data as {
      results: { name: string; count: number; not_signed_up_count: number }[];
    };
  };

  // Profile endpoints newer than the generated client (positions of
  // responsibility, interests, the completion checklist). Custom axios until
  // the typed client is regenerated from a spec that has them.
  private profileSectionRequest = async <T>(
    method: 'get' | 'post' | 'put' | 'delete',
    path: string,
    body?: object,
  ) => {
    const config = this.getAPIConfiguration();
    const res = await axios.request<T>({
      method,
      url: `${config.basePath}${path}`,
      data: body,
      headers: { Authorization: `Bearer ${this.apiKey}` },
    });
    return res.data;
  };

  // The profile's "auto-fill from resume": the server runs the same import as
  // the upload pipeline, filling only the sections the profile lacks.
  public autofillProfileFromResume = (resumeId: string) =>
    this.profileSectionRequest<{ success: boolean }>(
      'post',
      `/user/resume/${resumeId}/autofill`,
    );

  public addPositionOfResponsibility = (body: IPositionOfResponsibility) =>
    this.profileSectionRequest<{
      position_of_responsibility_obj?: IPositionOfResponsibility;
    }>('post', '/user/position-of-responsibility', body);

  public updatePositionOfResponsibility = (
    id: string,
    body: IPositionOfResponsibility,
  ) =>
    this.profileSectionRequest<{
      position_of_responsibility_obj?: IPositionOfResponsibility;
    }>('put', `/user/position-of-responsibility/${id}`, body);

  public deletePositionOfResponsibility = (id: string) =>
    this.profileSectionRequest<{ success: boolean }>(
      'delete',
      `/user/position-of-responsibility/${id}`,
    );

  public addInterest = (body: IInterest) =>
    this.profileSectionRequest<{ interest_obj?: IInterest }>(
      'post',
      '/user/interest',
      body,
    );

  public updateInterest = (id: string, body: IInterest) =>
    this.profileSectionRequest<{ interest_obj?: IInterest }>(
      'put',
      `/user/interest/${id}`,
      body,
    );

  public deleteInterest = (id: string) =>
    this.profileSectionRequest<{ success: boolean }>(
      'delete',
      `/user/interest/${id}`,
    );

  public getProfileCompletion = () =>
    this.profileSectionRequest<ProfileCompletion>(
      'get',
      '/user/profile-completion',
    );

  public setProfileItemSkipped = (body: {
    item: ProfileCompletionItemKey;
    skipped: boolean;
  }) =>
    this.profileSectionRequest<ProfileCompletion>(
      'put',
      '/user/profile-completion/skip',
      body,
    );

  // The dashboard's "Claim your certificate" cards. Its own request, apart from
  // /applicant/dashboard, because finding the user's tier ranks a whole
  // leaderboard. Custom axios until the typed client is regenerated.
  public getPendingCertificateClaims = async (signal?: AbortSignal) => {
    const config = this.getAPIConfiguration();
    const res = await axios.get(
      `${config.basePath}/applicant/dashboard/certificate-claims`,
      { signal, headers: { Authorization: `Bearer ${this.apiKey}` } },
    );
    return res.data as { claims: PendingCertificateClaimSub[] };
  };

  // ATS-board registry + metadata. Custom axios methods until the typed client
  // is regenerated from the spec (the board-metadata endpoints/fields are new).
  public listAtsBoards = async (params?: Record<string, any>) => {
    const config = this.getAPIConfiguration();
    const res = await axios.get(`${config.basePath}/admin/ats-boards`, {
      params,
      headers: { Authorization: `Bearer ${this.apiKey}` },
    });
    return res.data as { items: any[] };
  };

  // TurboHire market-intelligence (admin, read-only). Custom axios — internal
  // view, kept out of the typed spec.
  public getTurbohireAnalytics = async () => {
    const config = this.getAPIConfiguration();
    const res = await axios.get(
      `${config.basePath}/admin/turbohire-intel/analytics`,
      { headers: { Authorization: `Bearer ${this.apiKey}` } },
    );
    return res.data as {
      total_jobs: number;
      companies: number;
      fresh_30d: number;
      fresh_90d: number;
      with_posted_date: number;
      top_companies: { company: string; jobs: number }[];
      all_companies: { company: string; jobs: number }[];
    };
  };

  public getTurbohireJobs = async (params?: Record<string, any>) => {
    const config = this.getAPIConfiguration();
    const res = await axios.get(
      `${config.basePath}/admin/turbohire-intel/jobs`,
      { params, headers: { Authorization: `Bearer ${this.apiKey}` } },
    );
    return res.data as { total: number; jobs: any[] };
  };

  public getTurbohireJob = async (id: string) => {
    const config = this.getAPIConfiguration();
    const res = await axios.get(
      `${config.basePath}/admin/turbohire-intel/jobs/${id}`,
      { headers: { Authorization: `Bearer ${this.apiKey}` } },
    );
    return res.data as Record<string, any>;
  };

  // Abekus Pro+ upgrade blast from the store admin's premium item page.
  public premiumPromoPreflight = async (params: {
    item_id?: string;
    min_logins?: number | null;
    login_within_days?: number | null;
    max_credits?: number | null;
    offer_hours?: number | null;
    // The A/B arms' discounts, comma-separated ("0,10,20"); a blank slot leaves
    // that arm on the campaign default.
    discount_percents?: string | null;
  }) => {
    const api = await this.getPaymentApi();
    const res = await api.paymentApiPremiumPromoPreflight({
      discountPercents: params.discount_percents ?? null,
      itemId: params.item_id ?? null,
      loginWithinDays: params.login_within_days ?? null,
      maxCredits: params.max_credits ?? null,
      minLogins: params.min_logins ?? null,
      offerHours: params.offer_hours ?? null,
    });
    return res.data;
  };

  // Renders the email itself — no audience, nothing sent, nothing minted.
  public premiumPromoPreview = async (params: {
    item_id?: string;
    offer_hours?: number | null;
    discount_percent?: number | null;
  }) => {
    const api = await this.getPaymentApi();
    const res = await api.paymentApiPremiumPromoPreview({
      discountPercent: params.discount_percent ?? null,
      itemId: params.item_id ?? null,
      offerHours: params.offer_hours ?? null,
    });
    return res.data;
  };

  public sendPremiumPromoEmail = async (body: PremiumPromoSendBody) => {
    const api = await this.getPaymentApi();
    const res = await api.paymentApiSendPremiumPromoEmail({
      premiumPromoSendBody: body,
    });
    return res.data;
  };

  public censusAtsBoard = async (boardId: string) => {
    const config = this.getAPIConfiguration();
    const res = await axios.post(
      `${config.basePath}/admin/ats-boards/${boardId}/census`,
      {},
      { headers: { Authorization: `Bearer ${this.apiKey}` } },
    );
    return res.data;
  };

  public censusAllAtsBoards = async () => {
    const config = this.getAPIConfiguration();
    const res = await axios.post(
      `${config.basePath}/admin/ats-boards/census`,
      {},
      { headers: { Authorization: `Bearer ${this.apiKey}` } },
    );
    return res.data;
  };

  public targetedExternalIngest = async (selector: any, filters?: any) => {
    const config = this.getAPIConfiguration();
    const res = await axios.post(
      `${config.basePath}/admin/external-jobs/targeted-ingest`,
      { selector, filters },
      { headers: { Authorization: `Bearer ${this.apiKey}` } },
    );
    return res.data;
  };

  // "Jobs by score" list — catalogued postings ranked by demand score.
  public listExternalJobCatalog = async (params?: Record<string, any>) => {
    const config = this.getAPIConfiguration();
    const res = await axios.get(
      `${config.basePath}/admin/external-jobs/catalog`,
      { params, headers: { Authorization: `Bearer ${this.apiKey}` } },
    );
    return res.data as {
      items: any[];
      page: number;
      page_size: number;
      total: number;
    };
  };

  // Row counts per country behind the catalog's country picker. Takes the same
  // params as the listing (its `country` is ignored server-side), so each option
  // states what selecting it would give.
  public listExternalJobCatalogCountries = async (
    params?: Record<string, any>,
  ) => {
    const config = this.getAPIConfiguration();
    const res = await axios.get(
      `${config.basePath}/admin/external-jobs/catalog/countries`,
      { params, headers: { Authorization: `Bearer ${this.apiKey}` } },
    );
    return res.data as {
      items: { country: string; count: number }[];
      total: number;
      unresolved: number;
    };
  };

  // Score-ranked bulk fetch: ingest the top-N highest-demand unfetched postings.
  public fetchTopExternalJobs = async (count: number, filters?: any) => {
    const config = this.getAPIConfiguration();
    const res = await axios.post(
      `${config.basePath}/admin/external-jobs/fetch-top`,
      { count, filters },
      { headers: { Authorization: `Bearer ${this.apiKey}` } },
    );
    return res.data;
  };

  // Fetch exactly one catalog posting by id (per-row "fetch this one" action).
  public fetchOneExternalJob = async (catalogId: string) => {
    const config = this.getAPIConfiguration();
    const res = await axios.post(
      `${config.basePath}/admin/external-jobs/fetch-one`,
      { catalog_id: catalogId },
      { headers: { Authorization: `Bearer ${this.apiKey}` } },
    );
    return res.data;
  };

  // POST /campus-ambassador/join — become an ambassador (evergreen, join anytime).
  // Custom method until the typed client is regenerated from the spec.
  public joinCAProgram = async (
    payload: {
      college_id?: string | null;
      college_name?: string | null;
      whatsapp_number?: string | null;
      semester?: string | null;
    } = {},
  ) => {
    const config = this.getAPIConfiguration();
    const response = await axios.post(
      `${config.basePath}/campus-ambassador/join`,
      payload,
      { headers: { Authorization: `Bearer ${this.apiKey}` } },
    );
    return response.data;
  };

  // Record a click-through to an external job's apply page (the interest
  // signal). Custom method until the typed client is regenerated from the spec.
  public recordExternalJobClick = async (jobId: string) => {
    const config = this.getAPIConfiguration();
    const res = await axios.post(
      `${config.basePath}/job/${jobId}/external-click`,
      {},
      { headers: { Authorization: `Bearer ${this.apiKey}` } },
    );
    return res.data;
  };

  // Admin external-jobs statistics (clicks + applied over time). Custom method
  // until the typed client is regenerated from the spec.
  public getAdminExternalJobStatistics = async (
    params: Record<string, any>,
  ) => {
    const config = this.getAPIConfiguration();
    const res = await axios.get(
      `${config.basePath}/admin/external-jobs/statistics`,
      { params, headers: { Authorization: `Bearer ${this.apiKey}` } },
    );
    return res.data as { external_job_statistics: any[] };
  };

  // External (redirect-out) job self-report tracking. Custom methods until the
  // typed client is regenerated from the spec.
  public markExternalJobApplied = async (
    jobId: string,
    status?: string,
    // The resume the candidate applied with, so Applied Jobs records it the way
    // a native apply does. An id, not a URL — the server resolves it against
    // the caller's own resumes.
    resumeId?: string,
  ) => {
    const config = this.getAPIConfiguration();
    const res = await axios.post(
      `${config.basePath}/job/${jobId}/external-apply`,
      {
        ...(status ? { status } : {}),
        ...(resumeId ? { resume_id: resumeId } : {}),
      },
      { headers: { Authorization: `Bearer ${this.apiKey}` } },
    );
    return res.data;
  };

  public updateExternalApplicationStatus = async (
    jobId: string,
    status: string,
  ) => {
    const config = this.getAPIConfiguration();
    const res = await axios.put(
      `${config.basePath}/job/${jobId}/external-application`,
      { status },
      { headers: { Authorization: `Bearer ${this.apiKey}` } },
    );
    return res.data;
  };

  public removeExternalApplication = async (jobId: string) => {
    const config = this.getAPIConfiguration();
    const res = await axios.delete(
      `${config.basePath}/job/${jobId}/external-application`,
      { headers: { Authorization: `Bearer ${this.apiKey}` } },
    );
    return res.data;
  };

  // Replace one question of an employer's job assessment with a fresh, AI-reviewed
  // one in the same slot. Custom method until the typed client is regenerated.
  public regenerateJobChallengeQuestion = async (
    jobId: string,
    competitionId: string,
    questionId: string,
  ): Promise<RegeneratedJobChallengeQuestion> => {
    const config = this.getAPIConfiguration();
    const res = await axios.put(
      `${config.basePath}/job/${jobId}/competition/${competitionId}/question/${questionId}/regenerate`,
      {},
      { headers: { Authorization: `Bearer ${this.apiKey}` } },
    );
    return res.data;
  };

  // Referable entities for the Start-Referring picker (competitions with a live
  // alias; every candidate-visible job, searched and filtered server-side).
  // Custom methods until the typed client is regenerated.
  public getReferableCompetitions = async () => {
    const config = this.getAPIConfiguration();
    const response = await axios.get(
      `${config.basePath}/campus-ambassador/referable-competitions`,
      { headers: { Authorization: `Bearer ${this.apiKey}` } },
    );
    return response.data;
  };

  public getReferableJobs = async (
    params?: {
      search?: string;
      work_mode?: string;
      job_type?: string;
      location?: string;
    },
    signal?: AbortSignal,
  ) => {
    const config = this.getAPIConfiguration();
    const response = await axios.get(
      `${config.basePath}/campus-ambassador/referable-jobs`,
      {
        headers: { Authorization: `Bearer ${this.apiKey}` },
        // Empty values would fail the backend's OneOf validation, so only the
        // filters actually set are sent.
        params: Object.fromEntries(
          Object.entries(params ?? {}).filter(([, v]) => !!v),
        ),
        signal,
      },
    );
    return response.data;
  };

  public getReferableCourses = async () => {
    const config = this.getAPIConfiguration();
    const response = await axios.get(
      `${config.basePath}/campus-ambassador/referable-courses`,
      { headers: { Authorization: `Bearer ${this.apiKey}` } },
    );
    return response.data;
  };

  // Submit the composer's "Invite by email" batch for admin review (shared by
  // ambassadors and normal referrers). Custom method until the typed client is
  // regenerated.
  public submitReferralEmailInvites = async (payload: {
    emails: string[];
    style: string;
    target: string;
    entity_alias?: string | null;
    entity_label?: string | null;
  }) => {
    const config = this.getAPIConfiguration();
    const response = await axios.post(
      `${config.basePath}/referral/email-invites`,
      payload,
      { headers: { Authorization: `Bearer ${this.apiKey}` } },
    );
    return response.data;
  };

  // AI-generated share copy for the composer. Cached server-side per
  // (target, entity, platform, style) unless a custom prompt is given. Custom
  // method until the typed client is regenerated.
  public generateReferralMessage = async (
    payload: {
      target: string;
      platform: string;
      style: string;
      style_label?: string;
      length?: string;
      entity_alias?: string | null;
      entity_label?: string | null;
      entity_sub?: string | null;
      facts?: string | null;
      prompt?: string | null;
    },
    signal?: AbortSignal,
  ) => {
    const config = this.getAPIConfiguration();
    const response = await axios.post(
      `${config.basePath}/referral/message-suggestion`,
      payload,
      { headers: { Authorization: `Bearer ${this.apiKey}` }, signal },
    );
    return response.data;
  };

  // Distinct activity types (with counts, high→low) that actually occurred, for
  // the admin activity-log filter. Custom method until the typed client is
  // regenerated from the spec.
  public getActivityTypeCounts = async (days?: number) => {
    const config = this.getAPIConfiguration();
    const response = await axios.get(
      `${config.basePath}/admin/activity-logs/types`,
      {
        headers: { Authorization: `Bearer ${this.apiKey}` },
        params: days ? { days } : undefined,
      },
    );
    return response.data as {
      activity_types: { activity_type: string; count: number }[];
    };
  };

  // Course promotions — time-boxed free-to-use access + marketing blast. Custom
  // methods until the typed client is regenerated from the spec.
  public getCoursePromotion = async (skillId: string) => {
    const config = this.getAPIConfiguration();
    const response = await axios.get(
      `${config.basePath}/admin/courses/${skillId}/promotion`,
      { headers: { Authorization: `Bearer ${this.apiKey}` } },
    );
    return response.data;
  };

  public listCoursePromotions = async (skillId: string) => {
    const config = this.getAPIConfiguration();
    const response = await axios.get(
      `${config.basePath}/admin/courses/${skillId}/promotions`,
      { headers: { Authorization: `Bearer ${this.apiKey}` } },
    );
    return response.data;
  };

  public previewCoursePromotion = async (
    skillId: string,
    payload: { duration_days?: number; filters?: Record<string, unknown> },
  ) => {
    const config = this.getAPIConfiguration();
    const response = await axios.post(
      `${config.basePath}/admin/courses/${skillId}/promotion/preview`,
      payload,
      { headers: { Authorization: `Bearer ${this.apiKey}` } },
    );
    return response.data;
  };

  public sendCoursePromotionTestEmail = async (
    skillId: string,
    payload: { test_email: string; email: PromotionEmailPayload },
  ) => {
    const config = this.getAPIConfiguration();
    const response = await axios.post(
      `${config.basePath}/admin/courses/${skillId}/promotion/test-email`,
      payload,
      { headers: { Authorization: `Bearer ${this.apiKey}` } },
    );
    return response.data;
  };

  public createCoursePromotion = async (
    skillId: string,
    payload: {
      duration_days: number;
      filters?: Record<string, unknown>;
      email?: PromotionEmailPayload | null;
    },
  ) => {
    const config = this.getAPIConfiguration();
    const response = await axios.post(
      `${config.basePath}/admin/courses/${skillId}/promotion`,
      payload,
      { headers: { Authorization: `Bearer ${this.apiKey}` } },
    );
    return response.data;
  };

  // Free-unlock offers. Custom methods until the typed client is regenerated
  // from the spec.

  // The employer claims an offer for their org — `offerId`, or else the one the
  // dashboard is prompting for. Returns the same shape as GET /credits, with
  // `promo` describing the offer just claimed.
  public activateFreeUnlockOffer = async (orgId: string, offerId?: string) => {
    const config = this.getAPIConfiguration();
    const response = await axios.post(
      `${config.basePath}/org/${orgId}/credits/free-unlock-offer/activate`,
      { offer_id: offerId ?? null },
      { headers: { Authorization: `Bearer ${this.apiKey}` } },
    );
    return response.data;
  };

  // A signed-in member opened an offer's invite link: record the org as invited
  // (so every member gets the popup from now on). Same shape as GET /credits.
  public claimFreeUnlockOfferLink = async (orgId: string, offerId: string) => {
    const config = this.getAPIConfiguration();
    const response = await axios.post(
      `${config.basePath}/org/${orgId}/credits/free-unlock-offer/claim`,
      { offer_id: offerId },
      { headers: { Authorization: `Bearer ${this.apiKey}` } },
    );
    return response.data;
  };

  // Admin: every offer that hasn't been deleted, with its counts (sales too).
  public listFreeUnlockOffers = async (): Promise<FreeUnlockOffer[]> => {
    const config = this.getAPIConfiguration();
    const response = await axios.get(
      `${config.basePath}/admin/free-unlock-offers`,
      { headers: { Authorization: `Bearer ${this.apiKey}` } },
    );
    return response.data.data;
  };

  // Admin: create (no id) or update an offer.
  public saveFreeUnlockOffer = async (
    input: FreeUnlockOfferInput,
    offerId?: string,
  ): Promise<FreeUnlockOffer> => {
    const config = this.getAPIConfiguration();
    const headers = { Authorization: `Bearer ${this.apiKey}` };
    const response = offerId
      ? await axios.put(
          `${config.basePath}/admin/free-unlock-offers/${offerId}`,
          input,
          { headers },
        )
      : await axios.post(`${config.basePath}/admin/free-unlock-offers`, input, {
          headers,
        });
    return response.data;
  };

  // Admin: end a live offer now / delete one nobody is using.
  public endFreeUnlockOffer = async (
    offerId: string,
  ): Promise<FreeUnlockOffer> => {
    const config = this.getAPIConfiguration();
    const response = await axios.post(
      `${config.basePath}/admin/free-unlock-offers/${offerId}/end`,
      {},
      { headers: { Authorization: `Bearer ${this.apiKey}` } },
    );
    return response.data;
  };

  public deleteFreeUnlockOffer = async (offerId: string) => {
    const config = this.getAPIConfiguration();
    const response = await axios.delete(
      `${config.basePath}/admin/free-unlock-offers/${offerId}`,
      { headers: { Authorization: `Bearer ${this.apiKey}` } },
    );
    return response.data;
  };

  // An offer's invite list (admins and sales). Adding and removing take org ids
  // in the body and return the whole list, so callers just swap it in.
  public getFreeUnlockOfferInvites = async (
    offerId: string,
  ): Promise<FreeUnlockOfferInvite[]> => {
    const config = this.getAPIConfiguration();
    const response = await axios.get(
      `${config.basePath}/admin/free-unlock-offers/${offerId}/invites`,
      { headers: { Authorization: `Bearer ${this.apiKey}` } },
    );
    return response.data.data;
  };

  public changeFreeUnlockOfferInvites = async (
    offerId: string,
    change: 'add' | 'remove',
    orgIds: string[],
  ): Promise<FreeUnlockOfferInvitesChange> => {
    const config = this.getAPIConfiguration();
    const path = change === 'add' ? 'invites' : 'invites/remove';
    const response = await axios.post(
      `${config.basePath}/admin/free-unlock-offers/${offerId}/${path}`,
      { org_ids: orgIds },
      { headers: { Authorization: `Bearer ${this.apiKey}` } },
    );
    return response.data;
  };

  // Admin: how many employer orgs an audience (the editor's unsaved values)
  // reaches; `offerId` adds that offer's invite list.
  public getFreeUnlockOfferEmailReach = async (
    params: FreeUnlockEmailReachParams,
    checkDelivery: boolean,
  ): Promise<FreeUnlockEmailReach> => {
    const config = this.getAPIConfiguration();
    const response = await axios.get(
      `${config.basePath}/admin/free-unlock-offer/email-reach`,
      {
        params: {
          audience: params.rules.audience,
          start_date: params.startDate || undefined,
          end_date: params.endDate || undefined,
          created_within_days: params.rules.createdWithinDays,
          min_applications: params.rules.minApplications,
          min_jobs: params.rules.minJobs,
          offer_id: params.offerId,
          // The delivery check is the slow part; skipped for a counts-only call.
          send_emails: checkDelivery && params.sendEmails,
          email_prospects: checkDelivery && params.emailProspects,
          employer_email_limit: params.employerEmailLimit,
          prospect_email_limit: params.prospectEmailLimit,
          prospect_posted_within_days: params.prospectPostedWithinDays,
          prospect_added_within_days: params.prospectAddedWithinDays,
          prospect_job_role_categories: params.prospectJobRoleCategories,
          prospect_sources: params.prospectSources,
        },
        // Repeated keys (?x=a&x=b), which is how the API reads a list.
        paramsSerializer: { indexes: null },
        headers: { Authorization: `Bearer ${this.apiKey}` },
      },
    );
    return response.data;
  };

  public getFreeUnlockOfferAudienceReach = async (
    rules: FreeUnlockAudienceRules,
    startDate: string,
    offerId?: string,
  ): Promise<FreeUnlockAudienceReach> => {
    const config = this.getAPIConfiguration();
    const response = await axios.get(
      `${config.basePath}/admin/free-unlock-offer/audience`,
      {
        params: {
          audience: rules.audience,
          start_date: startDate || undefined,
          created_within_days: rules.createdWithinDays,
          min_applications: rules.minApplications,
          min_jobs: rules.minJobs,
          offer_id: offerId,
        },
        headers: { Authorization: `Bearer ${this.apiKey}` },
      },
    );
    return response.data;
  };

  // POST /custom-course/:buildId/unlock-lessons — the learner buys the next
  // batch of lessons in the course they forged. Custom method until the typed
  // client is regenerated from the spec.
  public unlockForgeLessons = async (
    buildId: string,
  ): Promise<ForgeUnlockResult> => {
    const config = this.getAPIConfiguration();
    const response = await axios.post(
      `${config.basePath}/custom-course/${buildId}/unlock-lessons`,
      {},
      { headers: { Authorization: `Bearer ${this.apiKey}` } },
    );
    return response.data;
  };

  // POST /custom-course/:buildId/answer — the learner's next message in the chat
  // before their plan is designed. Custom method until the typed client is
  // regenerated from the spec.
  public answerForgeCheck = async (
    buildId: string,
    answers: string[],
  ): Promise<GetCustomCourseResponse> => {
    const config = this.getAPIConfiguration();
    const response = await axios.post(
      `${config.basePath}/custom-course/${buildId}/answer`,
      { answers },
      { headers: { Authorization: `Bearer ${this.apiKey}` } },
    );
    return response.data;
  };

  // POST /custom-course/:buildId/retry — runs a forge step again once the server
  // says it is stuck. Custom method until the typed client is regenerated.
  public retryForge = async (
    buildId: string,
  ): Promise<GetCustomCourseResponse> => {
    const config = this.getAPIConfiguration();
    const response = await axios.post(
      `${config.basePath}/custom-course/${buildId}/retry`,
      {},
      { headers: { Authorization: `Bearer ${this.apiKey}` } },
    );
    return response.data;
  };

  // PUT /multiplayer/ai-invites/:batchId/accept — answer an AI player's game
  // invite: a room of the learner's own, or a seat in the batch's room.
  // Custom method until the typed client is regenerated from the spec.
  public acceptAiMultiplayerInvite = async (
    batchId: string,
  ): Promise<AcceptAiInviteResult> => {
    const config = this.getAPIConfiguration();
    const response = await axios.put(
      `${config.basePath}/multiplayer/ai-invites/${batchId}/accept`,
      {},
      { headers: { Authorization: `Bearer ${this.apiKey}` } },
    );
    return response.data;
  };

  // PUT /multiplayer/ai-invites/:batchId/respond — every answer but a join.
  // Custom method until the typed client is regenerated from the spec.
  public respondAiMultiplayerInvite = async (
    batchId: string,
    outcome: AiInviteResponse,
  ) => {
    const config = this.getAPIConfiguration();
    const response = await axios.put(
      `${config.basePath}/multiplayer/ai-invites/${batchId}/respond`,
      { outcome },
      { headers: { Authorization: `Bearer ${this.apiKey}` } },
    );
    return response.data;
  };

  // GET/PUT /admin/multiplayer/ai-invites/settings — the AI-invite settings
  // the AI Opponents screen owns, with a dry run of what they produce now.
  // POST /admin/multiplayer/ai-invites/run-now — one tick, now.
  // Custom methods until the typed client is regenerated from the spec.
  public getAiInviteSettings = async (): Promise<AiInviteSettingsResponse> => {
    const config = this.getAPIConfiguration();
    const response = await axios.get(
      `${config.basePath}/admin/multiplayer/ai-invites/settings`,
      { headers: { Authorization: `Bearer ${this.apiKey}` } },
    );
    return response.data;
  };

  public updateAiInviteSetting = async (
    name: string,
    value: string,
  ): Promise<AiInviteSettingsResponse> => {
    const config = this.getAPIConfiguration();
    const response = await axios.put(
      `${config.basePath}/admin/multiplayer/ai-invites/settings`,
      { name, value },
      { headers: { Authorization: `Bearer ${this.apiKey}` } },
    );
    return response.data;
  };

  public runAiInvitesNow = async (): Promise<AiInviteRunResult> => {
    const config = this.getAPIConfiguration();
    const response = await axios.post(
      `${config.basePath}/admin/multiplayer/ai-invites/run-now`,
      {},
      { headers: { Authorization: `Bearer ${this.apiKey}` } },
    );
    return response.data;
  };

  // ── Course / topic / subtopic intros ──────────────────────────────────────
  // Custom methods until the typed client is regenerated from the spec.

  // GET /course/:skillId/intro/:nodeId/content — the orientation deck for a
  // course, topic or subtopic. nodeId is the node's own skill id.
  public getIntroContent = async (
    skillId: string,
    nodeId: string,
    forceWelcome = false,
  ) => {
    const config = this.getAPIConfiguration();
    const response = await axios.get(
      `${config.basePath}/course/${skillId}/intro/${nodeId}/content`,
      {
        params: forceWelcome ? { force_welcome: true } : undefined,
        headers: { Authorization: `Bearer ${this.apiKey}` },
      },
    );
    return response.data;
  };

  // POST /course/:skillId/intro/:nodeId/complete — stop auto-opening this intro.
  public completeIntro = async (skillId: string, nodeId: string) => {
    const config = this.getAPIConfiguration();
    const response = await axios.post(
      `${config.basePath}/course/${skillId}/intro/${nodeId}/complete`,
      {},
      { headers: { Authorization: `Bearer ${this.apiKey}` } },
    );
    return response.data;
  };

  // PUT /admin/courses/:skillId/intro-visibility — show or hide a course's
  // intro deck for learners; the deck itself is kept.
  public setCourseIntroVisibility = async (
    skillId: string,
    showIntro: boolean,
  ) => {
    const config = this.getAPIConfiguration();
    const response = await axios.put(
      `${config.basePath}/admin/courses/${skillId}/intro-visibility`,
      { show_intro: showIntro },
      { headers: { Authorization: `Bearer ${this.apiKey}` } },
    );
    return response.data as { skill_id: string; show_intro: boolean };
  };

  // POST /admin/course-build/:jobId/continue — restart a build that stopped
  // part-way (e.g. across a deploy). Custom until the client is regenerated.
  public continueCourseBuild = async (jobId: string) => {
    const config = this.getAPIConfiguration();
    const response = await axios.post(
      `${config.basePath}/admin/course-build/${jobId}/continue`,
      {},
      { headers: { Authorization: `Bearer ${this.apiKey}` } },
    );
    return response.data as { jobs: number; lessons: number };
  };

  // POST /admin/course-build/continue-stalled — the same for every stopped build.
  public continueStalledCourseBuilds = async () => {
    const config = this.getAPIConfiguration();
    const response = await axios.post(
      `${config.basePath}/admin/course-build/continue-stalled`,
      {},
      { headers: { Authorization: `Bearer ${this.apiKey}` } },
    );
    return response.data as { jobs: number; lessons: number };
  };

  public adminGetCourseIntros = async (moduleId: string) => {
    const config = this.getAPIConfiguration();
    const response = await axios.get(
      `${config.basePath}/admin/skill/${moduleId}/intros`,
      { headers: { Authorization: `Bearer ${this.apiKey}` } },
    );
    return response.data;
  };

  public adminGenerateCourseIntros = async (
    moduleId: string,
    payload: { model_key?: string | null; regenerate?: boolean },
  ) => {
    const config = this.getAPIConfiguration();
    const response = await axios.post(
      `${config.basePath}/admin/skill/${moduleId}/intros/generate`,
      payload,
      { headers: { Authorization: `Bearer ${this.apiKey}` } },
    );
    return response.data;
  };

  public cancelCoursePromotion = async (
    skillId: string,
    promotionId: string,
  ) => {
    const config = this.getAPIConfiguration();
    const response = await axios.post(
      `${config.basePath}/admin/courses/${skillId}/promotion/${promotionId}/cancel`,
      {},
      { headers: { Authorization: `Bearer ${this.apiKey}` } },
    );
    return response.data;
  };
}

const apiClient = new ApiClient();

export default apiClient;
